import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/lib/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { ShieldCheck, Lock, Eye, EyeOff, Save } from "lucide-react";
import { toast } from "sonner";

function PwField({ label, value, onChange, show, onToggle, testid }) {
  return (
    <label className="block">
      <span className="text-[11px] font-mono-fin uppercase tracking-widest text-slate-500">{label}</span>
      <div className="relative mt-1.5">
        <input
          type={show ? "text" : "password"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testid}
          className="w-full px-4 py-2.5 pr-11 rounded-xl border border-slate-300 text-sm focus:border-blue-600 focus:ring-4 focus:ring-blue-100 outline-none"
        />
        <button type="button" onClick={onToggle} tabIndex={-1}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
          data-testid={`${testid}-toggle`}>
          {show ? <EyeOff size={16} /> : <Eye size={16} />}
        </button>
      </div>
    </label>
  );
}

function ChangePasswordCard() {
  const { t } = useTranslation();
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState({ cur: false, next: false, confirm: false });
  const [loading, setLoading] = useState(false);

  const tooShort = next.length > 0 && next.length < 6;
  const mismatch = confirm.length > 0 && next !== confirm;
  const valid = cur && next.length >= 6 && next === confirm;

  const submit = async (e) => {
    e.preventDefault();
    if (!valid) return;
    setLoading(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: next });
      toast.success(t("profile.pw_success", { defaultValue: "Contraseña actualizada correctamente" }));
      setCur(""); setNext(""); setConfirm("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <form onSubmit={submit} className="bg-white border border-slate-200 rounded-2xl p-5 sm:p-6" data-testid="change-password-card">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center shrink-0">
          <ShieldCheck className="text-emerald-600" size={20} />
        </div>
        <div>
          <h2 className="font-display font-bold text-lg text-slate-900">{t("profile.pw_title", { defaultValue: "Contraseña segura" })}</h2>
          <p className="text-xs text-slate-500">{t("profile.pw_sub", { defaultValue: "Cambia tu contraseña de acceso cuando quieras." })}</p>
        </div>
      </div>

      <div className="mt-5 space-y-3 max-w-md">
        <PwField label={t("profile.pw_current", { defaultValue: "Contraseña actual" })} value={cur} onChange={setCur}
          show={show.cur} onToggle={() => setShow((s) => ({ ...s, cur: !s.cur }))} testid="pw-current" />
        <PwField label={t("profile.pw_new", { defaultValue: "Nueva contraseña" })} value={next} onChange={setNext}
          show={show.next} onToggle={() => setShow((s) => ({ ...s, next: !s.next }))} testid="pw-new" />
        {tooShort && <p className="text-xs text-red-600 -mt-1" data-testid="pw-too-short">{t("profile.pw_min", { defaultValue: "Mínimo 6 caracteres." })}</p>}
        <PwField label={t("profile.pw_confirm", { defaultValue: "Repetir nueva contraseña" })} value={confirm} onChange={setConfirm}
          show={show.confirm} onToggle={() => setShow((s) => ({ ...s, confirm: !s.confirm }))} testid="pw-confirm" />
        {mismatch && <p className="text-xs text-red-600 -mt-1" data-testid="pw-mismatch">{t("profile.pw_mismatch", { defaultValue: "Las contraseñas no coinciden." })}</p>}
      </div>

      <div className="mt-5">
        <button type="submit" disabled={!valid || loading} data-testid="pw-submit"
          className="inline-flex items-center gap-2 bg-gradient-to-br from-emerald-600 to-blue-600 text-white font-semibold px-5 py-2.5 rounded-full text-sm disabled:opacity-50 disabled:cursor-not-allowed">
          {loading ? <Lock size={16} className="animate-pulse" /> : <Save size={16} />}
          {t("profile.pw_btn", { defaultValue: "Actualizar contraseña" })}
        </button>
      </div>
    </form>
  );
}

export default function Profile() {
  const { t } = useTranslation();
  const { user } = useAuth();
  if (!user) return null;
  const KYC_LABELS = {
    verified: t("profile.kyc_verified"),
    verification_required: t("profile.kyc_verification_required"),
    pending: t("profile.kyc_pending"),
    rejected: t("profile.kyc_rejected"),
  };
  const kycLabel = KYC_LABELS[user.kyc_status] || user.kyc_status || "—";
  const kycColor =
    user.kyc_status === "verified" ? "text-emerald-600"
    : user.kyc_status === "verification_required" ? "text-orange-600"
    : "text-slate-900";
  return (
    <div className="max-w-2xl space-y-6" data-testid="profile-page">
      <div>
        <div className="font-mono-fin text-xs uppercase tracking-widest text-blue-600">Perfil</div>
        <h1 className="font-display font-bold text-3xl md:text-4xl tracking-tighter text-slate-900 mt-1">Tus datos</h1>
      </div>
      <div className="bg-white border border-slate-200 rounded-2xl p-5 sm:p-6 grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
        {[
          ["Nombre", user.full_name, "text-slate-900"],
          ["Email", user.email, "text-slate-900"],
          ["País", user.country, "text-slate-900"],
          ["Rol", user.role, "text-slate-900"],
          ["Estado KYC", kycLabel, kycColor + " font-semibold"],
          ["Miembro desde", fmtDateTime(user.created_at), "text-slate-900"],
        ].map(([k, v, cls]) => (
          <div key={k} className="border-b border-slate-100 pb-2 min-w-0">
            <div className="text-[11px] font-mono-fin uppercase tracking-widest text-slate-500">{k}</div>
            <div className={`${cls} mt-1 break-words`} data-testid={`profile-${k.toLowerCase().replace(/\s+/g, "-")}`}>{v}</div>
          </div>
        ))}
      </div>

      <ChangePasswordCard />
    </div>
  );
}
